import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface HeaderProps {
  filters: {
    region: string;
    champion: string;
    role: string;
    patch?: string;
    timeRange: string;
  };
  setFilters: (filters: any) => void;
  comparisonMode: boolean;
  setComparisonMode: (mode: boolean) => void;
  playerA: string;
  setPlayerA: (name: string) => void;
  playerB: string;
  setPlayerB: (name: string) => void;
  pageContentRef: React.RefObject<HTMLDivElement>;
}

export function Header({ filters, setFilters, comparisonMode, setComparisonMode, playerA, setPlayerA, playerB, setPlayerB, pageContentRef }: HeaderProps) {
  const [_copiedLink, setCopiedLink] = useState(false);

  const _handleCopyLink = async () => {
    try {
      // Create ultra-compact URL format
      // Format: /#/a/[region]-[champion]-[role]-[patch]-[timeRange]-[comparisonMode]-[playerHash]
      // Single character codes for common values
      const regionCode = filters.region === 'NA' ? 'n' : filters.region === 'EUW' ? 'e' : filters.region === 'KR' ? 'k' : filters.region === 'CN' ? 'c' : filters.region[0].toLowerCase();
      const championCode = filters.champion === 'All' ? 'x' : filters.champion.substring(0, 3).toLowerCase();
      const roleCode = filters.role === 'All' ? 'x' : filters.role[0].toLowerCase();
      const patchCode = filters.patch ? filters.patch.replace('.', '') : '0000'; // Default to 0000 if no patch
      const timeCode = filters.timeRange;
      const compCode = comparisonMode ? '1' : '0';

      // Create short player name hash (first 3 chars, remove spaces)
      const playerHash = playerA.substring(0, 3).toLowerCase().replace(/\s+/g, '') || 'sum';

      // Build compact string: r-c-ro-pt-t-cm-p (always 7 parts separated by -)
      const compact = `${regionCode}-${championCode}-${roleCode}-${patchCode}-${timeCode}-${compCode}-${playerHash}`;

      const shareUrl = `${window.location.origin}/#/a/${compact}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/20 shadow-lg shadow-cyan-500/5">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Player Profiles */}
          <div className="flex items-center gap-6">
            {/* Player A */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-900">
                <AvatarImage src="https://images.unsplash.com/photo-1542751371-adc38448a05e?w=200&h=200&fit=crop" />
                <AvatarFallback>PA</AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-cyan-400 text-2xl">{playerA}</h1>
                <p className="text-slate-400">Master • 145 LP</p>
              </div>
            </div>

            {/* VS Indicator when in comparison mode */}
            {comparisonMode && (
              <>
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border border-purple-500/30">
                  <span className="text-purple-400">VS</span>
                </div>

                {/* Player B */}
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 ring-2 ring-fuchsia-400 ring-offset-2 ring-offset-slate-900">
                    <AvatarImage src="https://images.unsplash.com/photo-1534308143481-c55f00be8bd7?w=200&h=200&fit=crop" />
                    <AvatarFallback>PB</AvatarFallback>
                  </Avatar>
                  <div>
                    <h1 className="text-fuchsia-400 text-2xl">{playerB}</h1>
                    <p className="text-slate-400">Diamond II • 98 LP</p>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 pb-2 border-t border-slate-700/50 pt-4">
          <span className="text-slate-400 text-sm mr-2">Filters:</span>
          
          <Select value={filters.region} onValueChange={(value) => setFilters({ ...filters, region: value })}>
            <SelectTrigger className="w-24 bg-slate-800/50 border-slate-700 text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NA">NA</SelectItem>
              <SelectItem value="EUW">EUW</SelectItem>
              <SelectItem value="KR">KR</SelectItem>
              <SelectItem value="CN">CN</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.champion} onValueChange={(value) => setFilters({ ...filters, champion: value })}>
            <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700 text-slate-200">
              <SelectValue placeholder="Champion" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Champions</SelectItem>
              <SelectItem value="Yasuo">Yasuo</SelectItem>
              <SelectItem value="Zed">Zed</SelectItem>
              <SelectItem value="Ahri">Ahri</SelectItem>
              <SelectItem value="Jinx">Jinx</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.role} onValueChange={(value) => setFilters({ ...filters, role: value })}>
            <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700 text-slate-200">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Roles</SelectItem>
              <SelectItem value="Top">Top</SelectItem>
              <SelectItem value="Jungle">Jungle</SelectItem>
              <SelectItem value="Mid">Mid</SelectItem>
              <SelectItem value="Bot">Bot</SelectItem>
              <SelectItem value="Support">Support</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.timeRange} onValueChange={(value) => setFilters({ ...filters, timeRange: value })}>
            <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700 text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">Last 20</SelectItem>
              <SelectItem value="50">Last 50</SelectItem>
              <SelectItem value="100">Last 100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
